import { themeAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { TechFormSectionLoadError } from "../components/TechFormSectionLoadError";
import { Tq2BallastingCapacityGroup } from "../components/Tq2BallastingCapacityGroup";
import { Tq2BwtsDetailsGroup } from "../components/Tq2BwtsDetailsGroup";
import { Tq2HoldArrangementGroup } from "../components/Tq2HoldArrangementGroup";
import { Tq2ScrubberDetails } from "../components/Tq2ScrubberDetails";
import type { TechFormPageProps } from "../models/tech-form-page.models";
import { TECH_FORM_ENDPOINTS } from "../models/tech-form-section-data.constants";
import { TECH_FORM_RATING_OPTIONS } from "../models/tech-form-validation-ui.constants";
import { TECH_FORM_CACHE_MODES } from "../models/tech-form-vessel-cache.constants";
import {
  Tq2DesignDetailsM,
  designDetailsFormInit,
  mapSqlToTq2DesignDetailsForm,
  portBallastHoldNumberOptions,
  seaBallastHoldNumberOptions,
} from "../models/tq2-design-details.models";
import { OptionType } from "../models/types";
import { isTechFormSectionDataCached, loadTechFormEndpointData } from "../services/tech-form-section-data.service";
import { tq2DesignDetailsAtom } from "../state/tech-form.atoms";
import { ReactSelectStyling } from "../utils/TechUtils";
import { sanitizeTechFormInputForField, validateTq2DesignDetails } from "../utils/tech-form-validations";
import axios from "axios";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import { twMerge } from "tailwind-merge";

export const Tq2DesignDetails: React.FC<TechFormPageProps> = ({
  isCompletedForm,
  cacheInitialForm,
  onSectionReady,
}) => {
  const theme = useAtomValue(themeAtom);
  const { imoNumber, vesselName } = useParams();
  const [storedForm, setStoredForm] = useAtom(tq2DesignDetailsAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [isSectionDataReady, setIsSectionDataReady] = useState(false);
  const [sectionLoadError, setSectionLoadError] = useState(false);
  const [sectionLoadAttempt, setSectionLoadAttempt] = useState(0);
  const [form, setForm] = useState<Tq2DesignDetailsM>(designDetailsFormInit);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof Tq2DesignDetailsM, string>>>({});

  useEffect(() => {
    let ignoreResult = false;
    setSectionLoadError(false);
    const _form = storedForm ?? {};
    const storageisEmpty = Object.keys(_form).length === 0;

    if (isCompletedForm) {
      void fetchData(() => ignoreResult);
    } else {
      if (storageisEmpty) {
        void fetchData(() => ignoreResult);
      } else {
        setForm(_form);
        cacheInitialForm(_form);
        setIsSectionDataReady(true);
        onSectionReady?.();
      }
    }

    return () => {
      ignoreResult = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompletedForm, imoNumber, sectionLoadAttempt, vesselName]);

  useEffect(() => {
    setFormErrors(validateTq2DesignDetails(form));
  }, [form]);

  const fetchData = async (isStale: () => boolean) => {
    if (imoNumber && vesselName) {
      try {
        const requestIdentity = {
          imoNumber,
          vesselName,
          mode: isCompletedForm ? TECH_FORM_CACHE_MODES.completed : TECH_FORM_CACHE_MODES.editable,
        };
        if (!isTechFormSectionDataCached({ ...requestIdentity, section: 2 })) {
          setIsLoading(true);
        }
        const result = await loadTechFormEndpointData<any[]>({
          ...requestIdentity,
          endpoint: TECH_FORM_ENDPOINTS.vesselDesign,
        });
        if (isStale()) return;

        const _designDetials = result[0];
        const designDetials = mapSqlToTq2DesignDetailsForm(_designDetials);

        setForm(designDetials);
        setFormErrors(validateTq2DesignDetails(designDetials));
        setStoredForm(designDetials);
        cacheInitialForm(designDetials);
        setIsSectionDataReady(true);
        onSectionReady?.();
      } catch (error: unknown) {
        if (!isStale()) setSectionLoadError(true);
        if (axios.isAxiosError(error)) {
          console.error(error);
        } else {
          console.error("Unknown error", error);
        }
      } finally {
        if (!isStale()) {
          setIsLoading(false);
        }
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const updatedValue: any =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : sanitizeTechFormInputForField(name, value);

    setForm((prevState) => {
      const nextForm = {
        ...prevState,
        [name]: updatedValue,
      };
      setFormErrors(validateTq2DesignDetails(nextForm));
      return nextForm;
    });
  };

  const handlePortBallastHoldNumberChange = (newValue: OptionType | null) => {
    setForm((prevData) => ({
      ...prevData,
      portBallastHoldNumber: newValue || { value: "", label: "" },
    }));
  };

  const handleSeaBallastHoldNumberChange = (newValue: OptionType | null) => {
    setForm((prevData) => ({
      ...prevData,
      seaBallastHoldNumber: newValue || { value: "", label: "" },
    }));
  };

  const handleScrubberTypeChange = (newValue: OptionType | null) => {
    setForm((prevData) => ({
      ...prevData,
      scrubberType: newValue || { value: "", label: "" },
    }));
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors(validateTq2DesignDetails(form));
  };

  // Storage
  useEffect(() => {
    if (isSectionDataReady) {
      setStoredForm(form);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, isCompletedForm, isSectionDataReady]);

  const selectStyles = ReactSelectStyling();

  const formContent = (
    <div
      className={twMerge(
        "p-2 md:p-4",
        "[&_input]:border",
        "[&_select]:text-sm",
        theme.home.tailwind.panelBackground,
        theme.home.tailwind.textDefault,
        theme.balticQuestionnaire.labelsText,
        theme.balticQuestionnaire.inputsBg,
        theme.balticQuestionnaire.inputsText,
      )}
    >
      <h1 className="custom-heading pb-10">Design details</h1>
      <form onSubmit={handleSubmit}>
        <div>
          <Tq2HoldArrangementGroup>
            {/* Alternate hold loading */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Alternate hold loading</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="alternateHoldLoading"
                    value="yes"
                    checked={form.alternateHoldLoading === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="alternateHoldLoading"
                    value="no"
                    checked={form.alternateHoldLoading === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.alternateHoldLoading && (
                <p className="mt-1 text-xs text-red-600">{formErrors.alternateHoldLoading}</p>
              )}
            </div>

            {/* Port ballast hold number */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Port ballast hold number</label>
              <Select
                inputId="portBallastHoldNumber"
                classNamePrefix="rs"
                options={portBallastHoldNumberOptions}
                isSearchable={false}
                placeholder="Select an option"
                value={form.portBallastHoldNumber}
                onChange={handlePortBallastHoldNumberChange}
                styles={selectStyles}
              />
              {formErrors.portBallastHoldNumber && (
                <p className="mt-1 text-xs text-red-600">{formErrors.portBallastHoldNumber}</p>
              )}
            </div>

            {/* Sea ballast hold number */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Sea ballast hold number</label>
              <Select
                inputId="seaBallastHoldNumber"
                classNamePrefix="rs"
                options={seaBallastHoldNumberOptions}
                value={form.seaBallastHoldNumber}
                onChange={handleSeaBallastHoldNumberChange}
                isSearchable={false}
                placeholder="Select an option"
                styles={selectStyles}
              />
              {formErrors.seaBallastHoldNumber && (
                <p className="mt-1 text-xs text-red-600">{formErrors.seaBallastHoldNumber}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Number of cement holes</label>
              <div className="flex items-center">
                <input
                  type="text"
                  name="noofCementHoles"
                  value={form.noofCementHoles}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.noofCementHoles ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Dimensions of cement holes</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="dimensionsOfCementHoles"
                  value={form.dimensionsOfCementHoles}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.dimensionsOfCementHoles ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.dimensionsOfCementHoles ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  mm
                </span>
              </div>
            </div>
          </Tq2HoldArrangementGroup>

          <Tq2BwtsDetailsGroup>
            {/* Ballast Water Treatment System */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Ballast Water Treatment System</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="ballastWaterTreatmentSystem"
                    value="yes"
                    checked={form.ballastWaterTreatmentSystem === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="ballastWaterTreatmentSystem"
                    value="no"
                    checked={form.ballastWaterTreatmentSystem === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.ballastWaterTreatmentSystem && (
                <p className="mt-1 text-xs text-red-600">{formErrors.ballastWaterTreatmentSystem}</p>
              )}
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium">Required Salinity of the water</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="requiredSalinityOfWater"
                  value={form.requiredSalinityOfWater}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.requiredSalinityOfWater ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.requiredSalinityOfWater ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  ppt
                </span>
              </div>
              {formErrors.requiredSalinityOfWater && (
                <p className="mt-1 text-xs text-red-600">{formErrors.requiredSalinityOfWater}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Is the system able to work in fresh water?</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="isTheSystemAbleToWorkInFreshWater"
                    value="yes"
                    checked={form.isTheSystemAbleToWorkInFreshWater === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="isTheSystemAbleToWorkInFreshWater"
                    value="no"
                    checked={form.isTheSystemAbleToWorkInFreshWater === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.isTheSystemAbleToWorkInFreshWater && (
                <p className="mt-1 text-xs text-red-600">{formErrors.isTheSystemAbleToWorkInFreshWater}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Hold time (hours)</label>
              <div className="flex items-center">
                <input
                  type="text"
                  name="holdTimeDaysorHours"
                  value={form.holdTimeDaysorHours}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.holdTimeDaysorHours ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Ballasting rate using BWTS</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="ballastPumpsWaterTreatment"
                  value={form.ballastPumpsWaterTreatment}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.ballastPumpsWaterTreatment ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.ballastPumpsWaterTreatment ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  mt/hr
                </span>
              </div>
              {formErrors.ballastPumpsWaterTreatment && (
                <p className="mt-1 text-xs text-red-600">{formErrors.ballastPumpsWaterTreatment}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">De-ballasting rate using BWTS</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="deballastPumpsWaterTreatment"
                  value={form.deballastPumpsWaterTreatment}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.deballastPumpsWaterTreatment ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.deballastPumpsWaterTreatment ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  mt/hr
                </span>
              </div>
              {formErrors.deballastPumpsWaterTreatment && (
                <p className="mt-1 text-xs text-red-600">{formErrors.deballastPumpsWaterTreatment}</p>
              )}
            </div>
          </Tq2BwtsDetailsGroup>

          <Tq2BallastingCapacityGroup>
            <div className="mb-4">
              <label className="block text-sm font-medium">Ballasting rate using gravity</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="ballstingRateUsing"
                  value={form.ballstingRateUsing}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.ballstingRateUsing ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.ballstingRateUsing ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  mt/hr
                </span>
              </div>
              {formErrors.ballstingRateUsing && (
                <p className="mt-1 text-xs text-red-600">{formErrors.ballstingRateUsing}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">De-ballasting rate using gravity</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="deBallstingRateUsing"
                  value={form.deBallstingRateUsing}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.deBallstingRateUsing ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.deBallstingRateUsing ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  mt/hr
                </span>
              </div>
              {formErrors.deBallstingRateUsing && (
                <p className="mt-1 text-xs text-red-600">{formErrors.deBallstingRateUsing}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Ballasting rate using ballast pumps</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="ballastPumps"
                  value={form.ballastPumps}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.ballastPumps ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.ballastPumps ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m3/hrs
                </span>
              </div>
              {formErrors.ballastPumps && <p className="mt-1 text-xs text-red-600">{formErrors.ballastPumps}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">De-ballasting rate using ballast pumps</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="deBallastPumps"
                  value={form.deBallastPumps}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.deBallastPumps ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.deBallastPumps ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  m3/hrs
                </span>
              </div>
              {formErrors.deBallastPumps && <p className="mt-1 text-xs text-red-600">{formErrors.deBallastPumps}</p>}
            </div>
          </Tq2BallastingCapacityGroup>

          {/* EEDI/EVDI */}
          <div className="grid grid-cols-5 gap-4">
            <div className="mb-4">
              <label htmlFor="shipHasEedi" className="block text-sm font-medium">
                Ship has EEDI or EEXI Rating?
              </label>
              <select
                id="shipHasEedi"
                name="shipHasEedi"
                value={form.shipHasEedi}
                onChange={handleInputChange}
                aria-required="true"
                className="mt-1 w-full rounded border p-2"
              >
                <option value="">Select EEDI or EEXI</option>
                {form.shipHasEedi && !TECH_FORM_RATING_OPTIONS.some((option) => option.value === form.shipHasEedi) && (
                  <option value={form.shipHasEedi} disabled>
                    {form.shipHasEedi} — select EEDI or EEXI
                  </option>
                )}
                {TECH_FORM_RATING_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {formErrors.shipHasEedi && <p className="mt-1 text-xs text-red-600">{formErrors.shipHasEedi}</p>}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">EEDI or EEXI Vref speed per approved technical file</label>
              <div className="relative flex items-center">
                <input
                  type="text"
                  name="eediEvdiVrefSpeed"
                  value={form.eediEvdiVrefSpeed}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.eediEvdiVrefSpeed ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
                <span
                  className={twMerge(
                    "absolute bottom-0 right-0 top-0 mt-1 flex items-center rounded-r border p-2",
                    formErrors.eediEvdiVrefSpeed ? "border-red-600" : theme.uwhcAssistant.border,
                    theme.balticQuestionnaire.grayBg,
                  )}
                >
                  Kn
                </span>
              </div>
              {formErrors.eediEvdiVrefSpeed && (
                <p className="mt-1 text-xs text-red-600">{formErrors.eediEvdiVrefSpeed}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">
                Enter Attained EEDI or EEXI rating as per approved technical file
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  name="eediEvdirating"
                  value={form.eediEvdirating}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.eediEvdirating ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
              </div>
              {formErrors.eediEvdirating && <p className="mt-1 text-xs text-red-600">{formErrors.eediEvdirating}</p>}
            </div>

            {/* Shaft Power Meter */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Fitted with Shaft Power Meter?</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="fittedWithShaftPowerMeter"
                    value="yes"
                    checked={form.fittedWithShaftPowerMeter === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="fittedWithShaftPowerMeter"
                    value="no"
                    checked={form.fittedWithShaftPowerMeter === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.fittedWithShaftPowerMeter && (
                <p className="mt-1 text-xs text-red-600">{formErrors.fittedWithShaftPowerMeter}</p>
              )}
            </div>

            {/* Mass Flow Meters */}
            <div className="mb-4">
              <label className="block text-sm font-medium">
                Fitted with mass flow meters to measure fuel consumption?
              </label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="fittedWithMassFlowMetersToMeasureFuelConsumption"
                    value="yes"
                    checked={form.fittedWithMassFlowMetersToMeasureFuelConsumption === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="fittedWithMassFlowMetersToMeasureFuelConsumption"
                    value="no"
                    checked={form.fittedWithMassFlowMetersToMeasureFuelConsumption === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.fittedWithMassFlowMetersToMeasureFuelConsumption && (
                <p className="mt-1 text-xs text-red-600">
                  {formErrors.fittedWithMassFlowMetersToMeasureFuelConsumption}
                </p>
              )}
            </div>

            {/* Automatically recording performance data */}
            <div className="mb-4">
              <label className="block text-sm font-medium">
                Fitted with sensors/devices recording performance data?
              </label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="fittedWithThatisAutomaticallyRecording"
                    value="yes"
                    checked={form.fittedWithThatisAutomaticallyRecording === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="fittedWithThatisAutomaticallyRecording"
                    value="no"
                    checked={form.fittedWithThatisAutomaticallyRecording === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.fittedWithThatisAutomaticallyRecording && (
                <p className="mt-1 text-xs text-red-600">{formErrors.fittedWithThatisAutomaticallyRecording}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium">Sensors and Data Acquistion System Maker Name</label>
              <div className="flex items-center">
                <input
                  type="text"
                  name="sensorsAndDataAcquistionSystemMaker"
                  value={form.sensorsAndDataAcquistionSystemMaker}
                  onChange={handleInputChange}
                  className={`mt-1 w-full rounded-md border p-2 focus:outline-none ${
                    formErrors.sensorsAndDataAcquistionSystemMaker ? "border-red-600" : theme.uwhcAssistant.border
                  }`}
                />
              </div>
              {formErrors.sensorsAndDataAcquistionSystemMaker && (
                <p className="mt-1 text-xs text-red-600">{formErrors.sensorsAndDataAcquistionSystemMaker}</p>
              )}
            </div>

            {/* Scrubber fitted */}
            <div className="mb-4">
              <label className="block text-sm font-medium">Scrubber fitted</label>
              <div className="mt-1">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="scrubberFitted"
                    value="yes"
                    checked={form.scrubberFitted === "yes"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">Yes</span>
                </label>
                <label className="ml-4 inline-flex items-center">
                  <input
                    type="radio"
                    className="form-radio"
                    name="scrubberFitted"
                    value="no"
                    checked={form.scrubberFitted === "no"}
                    onChange={handleInputChange}
                  />
                  <span className="ml-2">No</span>
                </label>
              </div>
              {formErrors.scrubberFitted && <p className="mt-1 text-xs text-red-600">{formErrors.scrubberFitted}</p>}
            </div>

            {form.scrubberFitted === "yes" && (
              <Tq2ScrubberDetails
                form={form}
                formErrors={formErrors}
                onInputChange={handleInputChange}
                onTypeChange={handleScrubberTypeChange}
              />
            )}
          </div>
        </div>
      </form>
    </div>
  );

  if (sectionLoadError) {
    return <TechFormSectionLoadError onRetry={() => setSectionLoadAttempt((attempt) => attempt + 1)} />;
  }

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      {formContent}
    </LoadingWrapper>
  );
};
